import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { LogOut, Settings, Check } from 'lucide-react';
import { api } from '../App';
import { hasSubmodules, getSubmodules } from '../data/specialtyStructure';
import { ClinicalSpecialtyMenu } from './ClinicalSpecialtyMenu';

const EscapeRoomDashboard = ({ user, onLogout, showSubmodules = false, showCases = false, showModuleCases = false }) => {
  const navigate = useNavigate();
  const { module: urlModule, submodule: urlSubmodule } = useParams();
  const [loading, setLoading] = useState(true);
  const [cases, setCases] = useState([]);
  const [selectedModule, setSelectedModule] = useState(urlModule || null);
  const [selectedSubmodule, setSelectedSubmodule] = useState(urlSubmodule || null);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [passedCases, setPassedCases] = useState([]);
  const [passCounts, setPassCounts] = useState({});

  const specialtyItems = [
    { id: 'ginecologia', name: 'Ginecología y Obstetricia' },
    { id: 'cirugia', name: 'Cirugía' },
    { id: 'pediatria', name: 'Pediatría' },
    { id: 'medicina-interna', name: 'Medicina Interna' },
    { id: 'otros', name: 'Otros' }
  ];

  useEffect(() => {
    fetchCases();
    fetchProgress();
    fetchPassCounts();
  }, []);

  useEffect(() => {
    if (urlModule) {
      setSelectedModule(decodeURIComponent(urlModule));
    }
    if (urlSubmodule) {
      setSelectedSubmodule(decodeURIComponent(urlSubmodule));
    }
  }, [urlModule, urlSubmodule]);

  const fetchCases = async () => {
    try {
      setLoading(true);
      const response = await api.get('/cases');
      setCases(response.data);
    } catch (error) {
      console.error('Error fetching cases:', error);
      toast.error('Error al cargar casos clínicos');
    } finally {
      setLoading(false);
    }
  };

  const fetchPassCounts = async () => {
    try {
      const response = await api.get('/progress/pass-counts');
      setPassCounts(response.data.pass_counts || {});
    } catch (error) {
      console.error('Error fetching pass counts:', error);
    }
  };

  const fetchProgress = async () => {
    try {
      const response = await api.get('/progress/escape-rooms');
      setPassedCases(response.data.passed || []);
    } catch (error) {
      console.error('Error fetching progress:', error);
    }
  };

  const isPassed = (caseId) => passedCases.includes(caseId);

  const filteredCases = cases.filter(c => {
    if (selectedModule && c.module !== selectedModule) return false;
    if (selectedSubmodule && c.submodule !== selectedSubmodule) return false;
    return true;
  });

  const playPencilSound = () => {
    try {
      const audio = new Audio('https://customer-assets.emergentagent.com/job_13d67352-1aa7-430c-b46e-966b83d4fc8c/artifacts/wfi9fn7o_pencil_scribble_7-389260.mp3');
      audio.volume = 1.0;
      audio.play();
    } catch (error) {
      console.log('Audio not supported');
    }
  };

  const handleModuleClick = (moduleName) => {
    playPencilSound();
    setTimeout(() => {
      // Navegar siempre a la ruta del módulo, el componente decidirá qué mostrar
      navigate(`/escape-room/modules/${encodeURIComponent(moduleName)}`);
    }, 100);
  };

  const handleSubmoduleClick = (submodule) => {
    playPencilSound();
    setTimeout(() => {
      navigate(`/escape-room/modules/${encodeURIComponent(selectedModule)}/${encodeURIComponent(submodule)}`);
    }, 100);
  };

  const handleCaseClick = (caseId) => {
    playPencilSound();
    setTimeout(() => {
      navigate(`/escape-room/play/${caseId}`);
    }, 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Main modules view
  if (!showSubmodules && !showCases) {
    return (
      <div className="min-h-screen bg-white">
        {/* Header */}
        <header className="fixed top-0 left-0 right-0 py-4 px-4 z-50 bg-white">
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <button
              onClick={() => navigate('/')}
              className="flex items-center space-x-2 text-black font-medium text-sm hover:text-gray-600 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span>Menú Principal</span>
            </button>
            
            <div className="flex items-center space-x-2">
              {user.is_admin && (
                <button
                  onClick={() => navigate('/admin')}
                  className="bg-white rounded-full p-1.5 border border-black hover:bg-gray-100 transition-all"
                >
                  <Settings size={14} className="text-black" />
                </button>
              )}
              
              <button
                onClick={onLogout}
                className="bg-white rounded-full p-1.5 border border-black hover:bg-gray-100 transition-all"
              >
                <LogOut size={14} className="text-black" />
              </button>
            </div>
          </div>
        </header>

        <ClinicalSpecialtyMenu title="Casos clínicos Escape Room"
          subtitle="Simulacion con fines exclusivamente educativos. No sustituye el criterio clinico profesional."
          items={specialtyItems} onSelect={handleModuleClick} testIdPrefix="escape-module" titleTestId="escape-catalog-title" />
      </div>
    );
  }

  // Submodules view - Show cases directly if module has no submodules
  if (showSubmodules && !showCases && selectedModule && !hasSubmodules(selectedModule)) {
    const moduleCases = cases.filter(c => c.module === selectedModule);
    
    return (
      <div className="min-h-screen bg-white">
        <header className="fixed top-0 left-0 right-0 py-4 px-4 z-50 bg-white border-b border-gray-100">
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <button
              onClick={() => navigate('/escape-room')}
              className="flex items-center space-x-2 text-black font-medium text-sm hover:text-gray-600 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span>Volver</span>
            </button>
            
            <button
              onClick={onLogout}
              className="bg-white rounded-full p-1.5 border border-black hover:bg-gray-100 transition-all"
            >
              <LogOut size={14} className="text-black" />
            </button>
          </div>
        </header>

        <div className="pt-20 pb-8 px-4">
          <div className="max-w-2xl mx-auto">
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-black mb-6 text-center">
              {selectedModule}
            </h1>

            {moduleCases.length > 0 ? (
              <div className="space-y-3">
                {moduleCases.map((clinicalCase, index) => {
                  const isHovered = hoveredItem === clinicalCase.id;
                  const passed = isPassed(clinicalCase.id);
                  
                  return (
                    <button
                      key={clinicalCase.id}
                      onClick={() => handleCaseClick(clinicalCase.id)}
                      onMouseEnter={() => setHoveredItem(clinicalCase.id)}
                      onMouseLeave={() => setHoveredItem(null)}
                      className={`w-full border rounded-xl p-4 transition-all duration-200 text-left flex items-start space-x-3 ${
                        passed
                          ? 'bg-gray-100 border-gray-200'
                          : isHovered
                            ? 'bg-white border-black shadow-md'
                            : 'bg-white border-gray-200 hover:border-gray-300'
                      }`}
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      {/* Circle indicator */}
                      <div className="flex-shrink-0 mt-1">
                        <span 
                          className={`relative flex items-center justify-center w-5 h-5 rounded-full border-2 transition-all duration-200 ${
                            passed
                              ? 'border-gray-400'
                              : isHovered ? 'border-black' : 'border-gray-400'
                          }`}
                        >
                          <span 
                            className={`absolute w-3 h-3 rounded-full transition-all duration-200 ${
                              passed
                                ? 'bg-gray-400 scale-100 opacity-100'
                                : isHovered ? 'bg-black scale-100 opacity-100' : 'scale-0 opacity-0'
                            }`}
                          ></span>
                        </span>
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <h3 className={`text-sm sm:text-base font-semibold leading-snug transition-colors duration-200 ${
                          passed
                            ? 'text-gray-500'
                            : isHovered ? 'text-black' : 'text-gray-800'
                        }`}>
                          {clinicalCase.title}
                        </h3>
                        <p className={`text-xs sm:text-sm mt-1 ${passed ? 'text-gray-400' : 'text-gray-500'}`}>
                          {clinicalCase.steps_count ?? clinicalCase.steps?.length ?? 0} pasos
                        </p>
                      </div>
                      
                      {/* Checkmark for passed or Arrow */}
                      <div className="flex-shrink-0 self-center">
                        {passed ? (
                          <Check 
                            size={20} 
                            className="text-green-500"
                            strokeWidth={3}
                          />
                        ) : (
                          <svg 
                            className={`w-5 h-5 transition-all duration-200 ${
                              isHovered ? 'text-black translate-x-1' : 'text-gray-400'
                            }`}
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-600">No hay casos clínicos disponibles aún.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Submodules view
  if (showSubmodules && !showCases && selectedModule && hasSubmodules(selectedModule)) {
    const submodules = getSubmodules(selectedModule);
    
    return (
      <div className="min-h-screen bg-white">
        <header className="fixed top-0 left-0 right-0 py-4 px-4 z-50 bg-white">
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <button
              onClick={() => navigate('/escape-room')}
              className="flex items-center space-x-2 text-black font-medium text-sm hover:text-gray-600 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span>Volver</span>
            </button>
            
            <button
              onClick={onLogout}
              className="bg-white rounded-full p-1.5 border border-black hover:bg-gray-100 transition-all"
            >
              <LogOut size={14} className="text-black" />
            </button>
          </div>
        </header>

        <ClinicalSpecialtyMenu title={selectedModule} submodules
          items={submodules.map((name) => ({ name }))} onSelect={handleSubmoduleClick}
          testIdPrefix="escape-submodule" titleTestId="escape-submodule-title" />
      </div>
    );
  }

  // Module cases view (for modules without submodules like Ginecología, Pediatría)
  if (showModuleCases && selectedModule) {
    const moduleCases = cases.filter(c => c.module === selectedModule);
    
    return (
      <div className="min-h-screen bg-white">
        <header className="fixed top-0 left-0 right-0 py-4 px-4 z-50 bg-white border-b border-gray-100">
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <button
              onClick={() => navigate('/escape-room')}
              className="flex items-center space-x-2 text-black font-medium text-sm hover:text-gray-600 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span>Volver</span>
            </button>
            
            <button
              onClick={onLogout}
              className="bg-white rounded-full p-1.5 border border-black hover:bg-gray-100 transition-all"
            >
              <LogOut size={14} className="text-black" />
            </button>
          </div>
        </header>

        <div className="pt-20 pb-8 px-4">
          <div className="max-w-2xl mx-auto">
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-black mb-6 text-center">
              {selectedModule}
            </h1>

            {moduleCases.length > 0 ? (
              <div className="space-y-3">
                {moduleCases.map((clinicalCase, index) => {
                  const isHovered = hoveredItem === clinicalCase.id;
                  const passed = isPassed(clinicalCase.id);
                  
                  return (
                    <button
                      key={clinicalCase.id}
                      onClick={() => handleCaseClick(clinicalCase.id)}
                      onMouseEnter={() => setHoveredItem(clinicalCase.id)}
                      onMouseLeave={() => setHoveredItem(null)}
                      className={`w-full border rounded-xl p-4 transition-all duration-200 text-left flex items-start space-x-3 ${
                        passed
                          ? 'bg-gray-100 border-gray-200'
                          : isHovered
                            ? 'bg-white border-black shadow-md'
                            : 'bg-white border-gray-200 hover:border-gray-300'
                      }`}
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      {/* Circle indicator */}
                      <div className="flex-shrink-0 mt-1">
                        <span 
                          className={`relative flex items-center justify-center w-5 h-5 rounded-full border-2 transition-all duration-200 ${
                            passed
                              ? 'border-gray-400'
                              : isHovered ? 'border-black' : 'border-gray-400'
                          }`}
                        >
                          <span 
                            className={`absolute w-3 h-3 rounded-full transition-all duration-200 ${
                              passed
                                ? 'bg-gray-400 scale-100 opacity-100'
                                : isHovered ? 'bg-black scale-100 opacity-100' : 'scale-0 opacity-0'
                            }`}
                          ></span>
                        </span>
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <h3 className={`text-sm sm:text-base font-semibold leading-snug transition-colors duration-200 ${
                          passed
                            ? 'text-gray-500'
                            : isHovered ? 'text-black' : 'text-gray-800'
                        }`}>
                          {clinicalCase.title}
                        </h3>
                        <p className={`text-xs sm:text-sm mt-1 ${passed ? 'text-gray-400' : 'text-gray-500'}`}>
                          {clinicalCase.steps_count ?? clinicalCase.steps?.length ?? 0} pasos
                        </p>
                      </div>
                      
                      {/* Checkmark for passed or Arrow */}
                      <div className="flex-shrink-0 self-center">
                        {passed ? (
                          <Check 
                            size={20} 
                            className="text-green-500"
                            strokeWidth={3}
                          />
                        ) : (
                          <svg 
                            className={`w-5 h-5 transition-all duration-200 ${
                              isHovered ? 'text-black translate-x-1' : 'text-gray-400'
                            }`}
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-600">No hay casos clínicos disponibles aún.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Cases view
  if (showCases && selectedSubmodule) {
    return (
      <div className="min-h-screen bg-white">
        <header className="fixed top-0 left-0 right-0 py-4 px-4 z-50 bg-white border-b border-gray-100">
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <button
              onClick={() => navigate(`/escape-room/modules/${encodeURIComponent(selectedModule)}`)}
              className="flex items-center space-x-2 text-black font-medium text-sm hover:text-gray-600 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span>Volver</span>
            </button>
            
            <button
              onClick={onLogout}
              className="bg-white rounded-full p-1.5 border border-black hover:bg-gray-100 transition-all"
            >
              <LogOut size={14} className="text-black" />
            </button>
          </div>
        </header>

        <div className="pt-20 pb-8 px-4">
          <div className="max-w-2xl mx-auto">
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-black mb-6 text-center">
              {selectedSubmodule}
            </h1>

            {filteredCases.length > 0 ? (
              <div className="space-y-3">
                {filteredCases.map((clinicalCase, index) => {
                  const isHovered = hoveredItem === clinicalCase.id;
                  const passed = isPassed(clinicalCase.id);
                  const passCount = passCounts[clinicalCase.id] || 0;
                  
                  return (
                    <div key={clinicalCase.id} className="relative">
                      {/* Pass count badge - top right */}
                      {passCount > 0 && (
                        <div className="absolute -top-2 -right-2 z-10 flex items-center justify-center w-6 h-6 rounded-full bg-white border-2 border-blue-500">
                          <span className="text-xs font-bold text-blue-500">{passCount}</span>
                        </div>
                      )}
                      
                      <button
                        onClick={() => handleCaseClick(clinicalCase.id)}
                        onMouseEnter={() => setHoveredItem(clinicalCase.id)}
                        onMouseLeave={() => setHoveredItem(null)}
                        className={`w-full border rounded-xl p-4 transition-all duration-200 text-left flex items-start space-x-3 ${
                          passed
                            ? 'bg-gray-100 border-gray-200'
                            : isHovered
                              ? 'bg-white border-black shadow-md'
                              : 'bg-white border-gray-200 hover:border-gray-300'
                        }`}
                        style={{ animationDelay: `${index * 50}ms` }}
                      >
                        {/* Circle indicator */}
                        <div className="flex-shrink-0 mt-1">
                          <span 
                            className={`relative flex items-center justify-center w-5 h-5 rounded-full border-2 transition-all duration-200 ${
                              passed
                                ? 'border-gray-400'
                                : isHovered ? 'border-black' : 'border-gray-400'
                            }`}
                          >
                            <span 
                              className={`absolute w-3 h-3 rounded-full transition-all duration-200 ${
                                passed
                                  ? 'bg-gray-400 scale-100 opacity-100'
                                  : isHovered ? 'bg-black scale-100 opacity-100' : 'scale-0 opacity-0'
                              }`}
                            ></span>
                          </span>
                        </div>
                        
                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <h3 className={`text-sm sm:text-base font-semibold leading-snug transition-colors duration-200 ${
                            passed
                              ? 'text-gray-500'
                              : isHovered ? 'text-black' : 'text-gray-800'
                          }`}>
                            {clinicalCase.title}
                          </h3>
                          <p className={`text-xs sm:text-sm mt-1 ${passed ? 'text-gray-400' : 'text-gray-500'}`}>
                            {clinicalCase.steps_count ?? clinicalCase.steps?.length ?? 0} pasos
                          </p>
                        </div>
                        
                        {/* Checkmark for passed or Arrow */}
                        <div className="flex-shrink-0 self-center">
                          {passed ? (
                            <Check 
                              size={20} 
                              className="text-green-500"
                              strokeWidth={3}
                            />
                          ) : (
                            <svg 
                              className={`w-5 h-5 transition-all duration-200 ${
                                isHovered ? 'text-black translate-x-1' : 'text-gray-400'
                              }`}
                              fill="none" 
                              stroke="currentColor" 
                              viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        )}
                      </div>
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-600">No hay casos clínicos disponibles aún.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Default loading
  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
    </div>
  );
};

export default EscapeRoomDashboard;
